// src/api/centralBaseQuery.ts
//
// Shared RTK Query base query for all browser-direct Central API calls.
//
// Responsibilities:
//   1. Target `CENTRAL_URL`.
//   2. Attach the current user's Bearer access token on every request.
//   3. On a 401 response, single-flight-refresh the Central access token
//      via `proactiveRefreshCentralToken` (same mutex `baseApi` uses), then
//      retry the original request exactly once.
//
// Without (3) the new Central-direct APIs (members, billing, notifications,
// assets) would stay stuck on 401 after the token expires, because
// `fetchBaseQuery` alone has no refresh behavior. `baseApi` (which targets
// the CRM BFF at `/api`) already handles this for its own requests.

import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState, AppDispatch } from '../store/store';
import { CENTRAL_URL } from '../config/centralAuth';
import {
  selectCentralRefreshToken,
  updateCentralAccessToken,
} from '../store/slices/centralAuthSlice';
import { proactiveRefreshCentralToken } from './baseApi';

/**
 * Build an RTK Query base query that targets Central directly and handles
 * 401 via the shared refresh mutex. Callers can pass extra `prepareHeaders`
 * logic through the second argument.
 */
export function createCentralBaseQuery(options?: {
  /**
   * Optional path prefix appended to CENTRAL_URL (e.g. `/api/notifications`).
   * When set, endpoint paths become relative to that prefix.
   */
  pathPrefix?: string;
  prepareExtraHeaders?: (headers: Headers, getState: () => unknown) => void;
}): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
  const baseUrl = options?.pathPrefix
    ? `${CENTRAL_URL}${options.pathPrefix}`
    : CENTRAL_URL;
  const rawQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, api) => {
      const token = (api.getState() as RootState).centralAuth?.accessToken;
      if (token) headers.set('Authorization', `Bearer ${token}`);
      if (!headers.has('Accept')) headers.set('Accept', 'application/json');
      options?.prepareExtraHeaders?.(headers, api.getState);
      return headers;
    },
  });

  return async (args, api, extraOptions) => {
    let result = await rawQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      const state = api.getState() as RootState;
      const refreshToken = selectCentralRefreshToken(state);
      if (!refreshToken) return result;

      const refreshed = await proactiveRefreshCentralToken(refreshToken);
      if (refreshed) {
        (api.dispatch as AppDispatch)(
          updateCentralAccessToken({
            accessToken: refreshed.accessToken,
            expiresIn: refreshed.expiresIn,
            refreshToken: refreshed.refreshToken,
          }),
        );
        // Retry once with the new token — `prepareHeaders` reads
        // `centralAuth.accessToken` from the updated store state.
        result = await rawQuery(args, api, extraOptions);
      }
    }

    return result;
  };
}
